import { FileText, Loader2 } from "lucide-react";
import { useContext, type ReactElement } from "react";
import { SharepointFileContext } from "@/chat/SharepointFileContext";
import { useProjectFile } from "@/hooks/useProjectFile";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Parse a raw markdown citation string like `[fileName](ref:fileId#page=N)`
 * and extract the display name, file ID, and optional page number.
 */
function parseMarkdownCitation(raw: string): {
  fileName: string;
  sourceFileId?: string;
  pageNumber?: number;
} {
  const match = raw.match(/^\[([^\]]+)\]\(ref:([^)#]+)(?:#page=(\d+))?\)$/);
  if (match) {
    return {
      fileName: match[1],
      sourceFileId: match[2],
      pageNumber: match[3] ? parseInt(match[3], 10) : undefined,
    };
  }
  // Also strip a trailing `(ref:...)` if present without markdown link syntax
  const refSuffix = raw.match(/^(.+?)\s*\(ref:[^)]*\)$/);
  if (refSuffix) {
    return { fileName: refSuffix[1] };
  }
  return { fileName: raw };
}

interface CitationChipProps {
  fileName: string;
  sourceFileId?: string;
  pageNumber?: number;
  /** Whether to display the page number badge (e.g. "s.7"). Default true. */
  showPageNumber?: boolean;
}

const hasPage = (p: unknown): p is number => p !== null && p !== undefined;

function resolveMarkdownCitationProps(props: CitationChipProps): CitationChipProps {
  if (props.sourceFileId) return props;
  const parsed = parseMarkdownCitation(props.fileName);
  return {
    fileName: parsed.fileName,
    sourceFileId: parsed.sourceFileId ?? props.sourceFileId,
    pageNumber: parsed.pageNumber ?? props.pageNumber,
  };
}

/** Strip leaked markdown link syntax — `[text](ref:...)` → `text`. */
function cleanFileName(raw: string): string {
  return raw.replace(/\[([^\]]+)\]\(ref:[^)]+\)/g, "$1");
}

/** Strip extension, numeric prefix, and middle-truncate long names (file-explorer style). */
function formatShortName(fileName: string): string {
  const withoutExt = cleanFileName(fileName).replace(/\.[^.]+$/, "");
  const stripped = withoutExt
    .replace(/^[\d]+(?:[.-][\d]+)*[.-\s]+/, "") // strip leading numeric prefix like "01. ", "13.10 ", "2-1"
    .trim();
  // Only use stripped version if it keeps enough meaningful characters. For purely
  // numeric filenames like "156842-00-010-0001" stripping leaves "0001" which is
  // useless — fall back to the full stem so middle-truncation gives "156842-0…10-0001".
  const stem = stripped.length >= 6 ? stripped : withoutExt.trim();
  if (stem.length <= 20) return stem;
  return `${stem.slice(0, 8)}\u2026${stem.slice(-8)}`;
}

function useResolvedCitationUrl(sourceFileId?: string, pageNumber?: number) {
  const ctx = useContext(SharepointFileContext);
  const fileHook = ctx?.useProjectFile ?? useProjectFile;
  const { url, isLoading } = fileHook(sourceFileId ?? null);

  return {
    isLoading,
    resolvedUrl: url && hasPage(pageNumber) ? `${url}#page=${pageNumber}` : url,
  };
}

function CitationTooltipLabel({
  fileName,
  pageNumber,
  showPageNumber = true,
}: Pick<CitationChipProps, "fileName" | "pageNumber" | "showPageNumber">) {
  return (
    <span className="flex items-center gap-1.5">
      <FileText className="size-3 shrink-0" />
      <span className="max-w-[260px] truncate">{cleanFileName(fileName)}</span>
      {showPageNumber && hasPage(pageNumber) && (
        <span className="shrink-0 text-muted-foreground">s.{pageNumber}</span>
      )}
    </span>
  );
}

function CitationChipWrapper({
  fileName,
  pageNumber,
  showPageNumber = true,
  resolvedUrl,
  isLoading,
  children,
}: Pick<CitationChipProps, "fileName" | "pageNumber" | "showPageNumber"> & {
  resolvedUrl?: string;
  isLoading: boolean;
  children: ReactElement;
}) {
  if (isLoading) {
    return children;
  }

  const tooltipLabel = (
    <CitationTooltipLabel
      fileName={fileName}
      pageNumber={pageNumber}
      showPageNumber={showPageNumber}
    />
  );

  if (!resolvedUrl) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top">{tooltipLabel}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <a
          href={resolvedUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => event.stopPropagation()}
          className="relative inline-block"
        >
          {children}
        </a>
      </TooltipTrigger>
      <TooltipContent side="top">{tooltipLabel}</TooltipContent>
    </Tooltip>
  );
}

export function InlineCitationChip(rawProps: CitationChipProps) {
  const { fileName, sourceFileId, pageNumber } = resolveMarkdownCitationProps(rawProps);
  const showPage = rawProps.showPageNumber ?? true;
  const { isLoading, resolvedUrl } = useResolvedCitationUrl(sourceFileId, pageNumber);
  const interactive = Boolean(resolvedUrl);

  return (
    <CitationChipWrapper
      fileName={fileName}
      pageNumber={pageNumber}
      showPageNumber={showPage}
      resolvedUrl={resolvedUrl}
      isLoading={isLoading}
    >
      <InlineCitationChipContainer interactive={interactive}>
        <span className="max-w-[120px] truncate">{formatShortName(fileName)}</span>
        {showPage && hasPage(pageNumber) && (
          <span
            className={cn(
              "shrink-0 text-muted-foreground",
              interactive && "group-hover/citation-chip:text-info-foreground/80",
            )}
          >
            s.{pageNumber}
          </span>
        )}
      </InlineCitationChipContainer>
    </CitationChipWrapper>
  );
}

export function DocumentCitationChip(rawProps: CitationChipProps) {
  const { fileName, sourceFileId, pageNumber } = resolveMarkdownCitationProps(rawProps);
  const showPage = rawProps.showPageNumber ?? true;
  const { isLoading, resolvedUrl } = useResolvedCitationUrl(sourceFileId, pageNumber);
  const interactive = Boolean(resolvedUrl);

  return (
    <CitationChipWrapper
      fileName={fileName}
      pageNumber={pageNumber}
      showPageNumber={showPage}
      resolvedUrl={resolvedUrl}
      isLoading={isLoading}
    >
      <DocumentCitationChipContainer interactive={interactive}>
        {isLoading ? (
          <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
        ) : (
          <FileText
            className={cn(
              "h-3 w-3 shrink-0 text-muted-foreground",
              interactive && "group-hover/citation-chip:text-info-foreground",
            )}
          />
        )}
        <span className="max-w-[320px] truncate">{cleanFileName(fileName)}</span>
        {showPage && hasPage(pageNumber) && (
          <span
            className={cn(
              "shrink-0 text-muted-foreground",
              interactive && "group-hover/citation-chip:text-info-foreground/80",
            )}
          >
            s.{pageNumber}
          </span>
        )}
      </DocumentCitationChipContainer>
    </CitationChipWrapper>
  );
}

function InlineCitationChipContainer({
  children,
  className,
  interactive,
}: {
  children: React.ReactNode;
  className?: string;
  interactive?: boolean;
}) {
  return (
    <span
      className={cn(
        "group/citation-chip inline-flex items-center gap-1 rounded-sm bg-muted p-0.75 font-mono text-[0.7em] leading-none text-muted-foreground align-baseline",
        interactive && "cursor-pointer hover:bg-info hover:text-info-foreground",
        className,
      )}
      data-citation-chip
      data-is-interactive={interactive}
    >
      {children}
    </span>
  );
}

function DocumentCitationChipContainer({
  children,
  className,
  interactive,
}: {
  children: React.ReactNode;
  className?: string;
  interactive?: boolean;
}) {
  return (
    <span
      className={cn(
        "group/citation-chip inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80",
        interactive && "cursor-pointer hover:bg-info hover:text-info-foreground",
        className,
      )}
      data-citation-chip
      data-is-interactive={interactive}
    >
      {children}
    </span>
  );
}
